/**
 * One exercise plan, whole — which is the only way to learn what is on one.
 *
 * The list row carries a count and a total but no exercises, so opening a plan
 * is a call — cached under the plan's id, so reopening one this sitting has
 * already read is instant. Writing to a plan clears that cache, which is what
 * keeps a reopened plan from showing the lines it had before the save; see
 * `EXERCISES_QUERY_KEY`.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchExercisePlan } from "../services/exercisePlansService";
import { EXERCISES_QUERY_KEY } from "./useExercisePlans";
import type { ExercisePlanRecord } from "../types/exercisePlans.types";

export type ExercisePlanStatus = "loading" | "ready" | "failed";

export interface ExercisePlanState {
  /** `undefined` until it has loaded, and for good if `status` is not ready. */
  plan: ExercisePlanRecord | undefined;
  status: ExercisePlanStatus;
  /** Set only when `status` is `failed` — what the dialog shows. */
  error?: string;
  reload: () => void;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

export function useExercisePlan(
  planId: string | undefined
): ExercisePlanState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: [...EXERCISES_QUERY_KEY, "detail", planId ?? null] as const,
    queryFn: ({ signal }) => fetchExercisePlan(getToken, planId!, signal),
    // Nothing to ask about with no plan open.
    enabled: !!planId,
  });

  useEffect(() => {
    if (query.isError)
      console.error("An exercise plan failed to load:", query.error);
  }, [query.isError, query.error]);

  return {
    plan: query.data,
    status: query.isError
      ? "failed"
      : query.isPending || query.isFetching
      ? "loading"
      : "ready",
    error: query.isError ? failureMessage(query.error) : undefined,
    reload: () => {
      void query.refetch();
    },
  };
}
